import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Checkbox, Input, Toast } from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { EventImageUploadField } from "@/components/Events/EventImageUploadField";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import { eventHeroBannerService, type EventHeroBanner } from "@/api/events.service";
import type { ImageRef } from "@/api/events/files";
import {
  validateHeroBannerForm,
  type HeroBannerFormErrors,
  type HeroBannerFormValues,
} from "./eventValidation";
import { describeHeroBannerVisibility } from "./heroBannerVisibility";
import styles from "./Events.module.css";

/** The ids the visibility control points `aria-describedby` at, so the state reaches the control. */
const VISIBILITY_BADGE_ID = "hero-banner-visibility-state";
const VISIBILITY_HINT_ID = "hero-banner-visibility-hint";

const EMPTY_FORM: HeroBannerFormValues = {
  backgroundImage: null,
  title: "",
  dateText: "",
  locationText: "",
  ctaText: "",
  ctaUrl: "",
  // A new banner goes up when it is saved, matching content-api's `DEFAULT true`. Hiding is
  // something staff do on purpose, never the starting position.
  showOnSite: true,
};

function toFormValues(banner: EventHeroBanner | null): HeroBannerFormValues {
  if (!banner) return { ...EMPTY_FORM };
  return {
    backgroundImage: banner.backgroundImage,
    title: banner.title,
    dateText: banner.dateText,
    locationText: banner.locationText,
    ctaText: banner.ctaText,
    ctaUrl: banner.ctaUrl,
    showOnSite: banner.showOnSite,
  };
}

function HeroBannerForm({
  banner,
  canWrite,
  onSaved,
}: {
  banner: EventHeroBanner | null;
  canWrite: boolean;
  onSaved: (message: string) => void;
}) {
  const [values, setValues] = useState<HeroBannerFormValues>(() => toFormValues(banner));
  const [errors, setErrors] = useState<HeroBannerFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const setField = <K extends keyof HeroBannerFormValues>(
    key: K,
    value: HeroBannerFormValues[K],
  ) => {
    setValues(current => ({ ...current, [key]: value }));
    setErrors(current => ({ ...current, [key]: undefined }));
  };

  const handleSubmit = async (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault();

    const nextErrors = validateHeroBannerForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      await eventHeroBannerService.save({
        // Validation above guarantees the image is present.
        backgroundImage: values.backgroundImage as ImageRef,
        title: values.title.trim(),
        dateText: values.dateText.trim(),
        locationText: values.locationText.trim(),
        ctaText: values.ctaText.trim(),
        ctaUrl: values.ctaUrl.trim(),
        showOnSite: values.showOnSite,
      });
      onSaved(values.showOnSite ? "Hero banner saved." : "Hero banner saved and hidden.");
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "The banner could not be saved");
    } finally {
      setSaving(false);
    }
  };

  const visibility = describeHeroBannerVisibility({
    // Straight from the loaded banner rather than from state, so it re-reads after a save.
    saved: banner ? banner.showOnSite : null,
    pending: values.showOnSite,
  });

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <EventImageUploadField
        label="Background image"
        value={values.backgroundImage}
        slug={values.title}
        disabled={!canWrite}
        onChange={ref => setField("backgroundImage", ref)}
      />
      {errors.backgroundImage && (
        <p className={styles.error} role="alert">
          {errors.backgroundImage}
        </p>
      )}

      <Input
        label="Title"
        value={values.title}
        error={errors.title}
        fullWidth
        disabled={!canWrite}
        onChange={e => setField("title", e.target.value)}
      />

      <Input
        label="Date text"
        value={values.dateText}
        error={errors.dateText}
        fullWidth
        disabled={!canWrite}
        helperText="Plain display text, not a date field — write it however it reads best, e.g. 22 September 2026."
        onChange={e => setField("dateText", e.target.value)}
      />

      <Input
        label="Location text"
        value={values.locationText}
        error={errors.locationText}
        fullWidth
        disabled={!canWrite}
        helperText="Plain display text, e.g. Park Plaza Westminster Bridge London."
        onChange={e => setField("locationText", e.target.value)}
      />

      <Input
        label="CTA text"
        value={values.ctaText}
        error={errors.ctaText}
        fullWidth
        disabled={!canWrite}
        onChange={e => setField("ctaText", e.target.value)}
      />

      <Input
        label="CTA URL"
        type="url"
        value={values.ctaUrl}
        error={errors.ctaUrl}
        fullWidth
        disabled={!canWrite}
        helperText="The whole banner links here."
        onChange={e => setField("ctaUrl", e.target.value)}
      />

      {/*
        Last thing before the button, because publishing and hiding only ever happen on save:
        the badge already reads "Hidden · save to publish", so the state and the control that
        applies it belong in one footer rather than at opposite ends of the form. The spec's
        requirement — that a hidden banner is obvious, when its fields look exactly like a live
        one's — is carried by the badge's own words; the tone class only reinforces them.
      */}
      <section className={styles.visibility} aria-labelledby="hero-banner-visibility-heading">
        <div className={styles.visibilityHeader}>
          <h2 className={styles.visibilityHeading} id="hero-banner-visibility-heading">
            Visibility
          </h2>
          <span
            id={VISIBILITY_BADGE_ID}
            className={`${styles.visibilityBadge} ${styles[`visibility_${visibility.tone}`]}`}
            role="status"
          >
            {visibility.badge}
          </span>
        </div>

        <Checkbox
          label="Show on site"
          /*
           * The design system's Checkbox renders its visible text as a plain sibling <span>, so
           * the input has no accessible name of its own — this supplies one, and points at the
           * badge so a screen reader reads the current state with the control.
           */
          aria-label="Show on site"
          aria-describedby={
            visibility.hint ? `${VISIBILITY_BADGE_ID} ${VISIBILITY_HINT_ID}` : VISIBILITY_BADGE_ID
          }
          checked={values.showOnSite}
          disabled={!canWrite}
          onChange={e => setField("showOnSite", e.target.checked)}
        />

        {visibility.hint && (
          <p id={VISIBILITY_HINT_ID} className={styles.visibilityHint}>
            {visibility.hint}
          </p>
        )}
      </section>

      {saveError && (
        <p className={styles.error} role="alert">
          {saveError}
        </p>
      )}

      <div className={styles.formActions}>
        <Button type="submit" variant="contained" color="primary" disabled={!canWrite || saving}>
          {saving ? "Saving…" : "Save banner"}
        </Button>
      </div>
    </form>
  );
}

export function HeroBannerPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("cms.events.read");
  const canWrite = hasPermission("cms.events.write");
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const { data, loading, error, refetch } = useQuery(() => eventHeroBannerService.get(), []);

  if (!canRead) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Events hero banner"
        subtitle="The sponsored placement above the events grid. Its date and location are plain display text — they advertise one event in whatever wording reads best, not a second event record."
      >
        <Link className={styles.bannerLink} to={ROUTES.events.list}>
          Back to events
        </Link>
      </PageHeader>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={loading} />}

      {loading ? (
        <LoadingState message="Loading the hero banner…" />
      ) : (
        <HeroBannerForm
          // Remount once the saved banner has loaded, so the form starts from it.
          key={data?.heroBanner?.id ?? "new"}
          banner={data?.heroBanner ?? null}
          canWrite={canWrite}
          onSaved={message => {
            setToast({ message, type: "success" });
            refetch();
          }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default HeroBannerPage;
